import { DestroyRef, inject, Injectable, Injector, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, type NavigationExtras, type Params, Router } from '@angular/router';
import { type Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { type NavigationExtrasState, type PaginationParameters as PaginationOptions } from '../models';

@Injectable({
    providedIn: 'root',
})
export class PaginationService {
    public static readonly defaultOptions = {
        scrollOnNavigation: true,
        scrollOnPagination: true,
    };

    public readonly page = signal<number>(1);
    private readonly destroyRef = inject(DestroyRef);
    private readonly injector = inject(Injector);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);

    public constructor() {
        this.route.queryParams
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                distinctUntilChanged((previous, current) => previous.page === current.page),
            )
            .subscribe((params: Params) => {
                this.page.set(parseInt(params.page ?? 1, 10));
            });
    }

    public getPage(): Observable<number> {
        return toObservable(this.page, { injector: this.injector });
    }

    public setPage(
        page: number,
        options: PaginationOptions = PaginationService.defaultOptions,
        queryParams?: Params,
        stateOptions?: Partial<NavigationExtrasState>,
    ): void {
        const scrollOnNavigation = options.scrollOnNavigation ?? PaginationService.defaultOptions.scrollOnNavigation;
        const scrollOnPagination = options.scrollOnPagination ?? PaginationService.defaultOptions.scrollOnPagination;

        let state: NavigationExtrasState = {
            doNotScroll: !scrollOnNavigation,
            paginationChanged: scrollOnPagination,
        };

        if (stateOptions) {
            state = { ...state, ...stateOptions };
        }

        const navigationExtras: NavigationExtras = {
            relativeTo: this.route,
            queryParams: queryParams ? { ...queryParams, page } : { page },
            queryParamsHandling: 'merge',
            state,
        };

        void this.router.navigate([], navigationExtras);
    }

    public resetPage(options: PaginationOptions = PaginationService.defaultOptions, queryParams?: Params, stateOptions?: Partial<NavigationExtrasState>): void {
        this.setPage(1, options, queryParams, stateOptions);
    }
}
